import moment from 'moment';
import { EMPTY_VALUE } from '../internalRoles';
import { PROFILE_DATE_TIME_FORMAT } from './dateFormat';
import { UserAdapters } from './User';

/**
 * The header's name for an identity. Both name halves are nullable in
 * auth-service, so a sparse identity falls back to its email, which is how an
 * admin found it in the first place.
 */
const toName = ({ firstName, lastName, email }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim() || email || EMPTY_VALUE;

const toDateTimeLabel = (isoDate) =>
  (isoDate ? moment(isoDate).format(PROFILE_DATE_TIME_FORMAT) : EMPTY_VALUE);

/**
 * Maps one auth-service user onto the Location User profile.
 *
 * Last Login reads `lastActiveAt`, which auth-service moves on every sign-in and
 * token refresh — the closest thing it records to a last login.
 *
 * @param {import('../types/User').IUserSummaryViewEntity} user
 * @returns {import('../types/User').ILocationUserProfile}
 */
export const toLocationUserProfile = (user) => ({
  id: user.id,
  name: toName(user),
  firstName: user.firstName ?? null,
  lastName: user.lastName ?? null,
  email: user.email ?? null,
  phoneNumber: user.phoneNumber ?? null,
  phoneLabel: UserAdapters.toPhoneLabel(user.phoneNumber),
  createdAtLabel: toDateTimeLabel(user.createdAt),
  lastLoginLabel: toDateTimeLabel(user.lastActiveAt),
  timeZoneLabel: user.timeZone || EMPTY_VALUE,
});

/**
 * A location's address as one line. organization-service's formatted line wins;
 * without one, every part on record in postal order ("1 Main St, Austin, TX
 * 78701, US"), so a location whose city is known never reads as having no
 * address. Every part is nullable, and all of them are null when nothing is on
 * record.
 *
 * @param {import('../types/User.api').IIdentityLocationAddressApiModel} address
 */
const toAddressLabel = ({
  formattedAddress, streetNumber, street, city, state, postalCode, country,
}) => {
  if (formattedAddress) return formattedAddress;

  const parts = [
    [streetNumber, street].filter(Boolean).join(' '),
    city,
    [state, postalCode].filter(Boolean).join(' '),
    country,
  ].filter(Boolean);

  return parts.length ? parts.join(', ') : EMPTY_VALUE;
};

/**
 * How an identity reaches a location, as the Access cell reads it: "Direct",
 * or "Indirect" with the group or organization it comes from beneath, since
 * that is what an admin has to change to remove the access. Both the Location
 * User's Locations tab and the office profile's Location Admins tab show it.
 * organization-service sends no name for a location context, and a null one
 * when that group or organization no longer exists; either reads "Indirect"
 * alone.
 *
 * @param {import('../types/User.api').IIdentityLocationSourceApiModel} [source]
 * @returns {{ accessLabel: string, accessViaLabel: string }}
 */
export const toAccessLabels = (source) => {
  const isInherited = source?.type === 'inherited';
  const viaName = isInherited ? source.fromContextName : null;

  return {
    accessLabel: isInherited ? 'Indirect' : 'Direct',
    accessViaLabel: viaName ? `via ${viaName}` : '',
  };
};

/**
 * Maps one location an identity can access onto a Locations tab row.
 *
 * @param {import('../types/User.api').IIdentityLocationApiModel} location
 * @returns {import('../types/User').IIdentityLocationRow}
 */
export const toIdentityLocationRow = (location) => ({
  id: location.locationId,
  name: location.name || EMPTY_VALUE,
  officeId: location.registermodelId ?? null,
  addressLabel: toAddressLabel(location.address ?? {}),
  ...toAccessLabels(location.source),
});

/**
 * Transforms a page of an identity's locations into a page of Locations tab
 * rows. `total` defaults to 0 for the reason `toGetInternalUsersPageResult`
 * gives.
 *
 * @param {import('../types/User.api').IGetIdentityLocationsApiResponse} response
 * @returns {import('../types/User').IGetIdentityLocationsPageResult}
 */
export const toIdentityLocationsPageResult = (response) => ({
  data: (response?.items ?? []).map(toIdentityLocationRow),
  total: response?.total ?? 0,
});

/**
 * Location User adapters, grouped to match the `UserAdapters` convention.
 */
export const LocationUserAdapters = {
  toLocationUserProfile,
  toAccessLabels,
  toIdentityLocationRow,
  toIdentityLocationsPageResult,
};
